import type { ColumnDef } from '@tanstack/react-table';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { StackedCellText } from './StackedCellText';

const AREA_NAME_SEPARATOR = ', ';
const STACKED_AREA_HEADER = 'Area';
const COMMA_SEPARATED_AREA_HEADER = 'Thana / District / Division';

interface AreaNames {
  divisionName: string | null;
  districtName: string | null;
  thanaName?: string | null | undefined;
}

interface AreaColumnOptions {
  searchText?: string | undefined;
  isCommaSeparated?: boolean | undefined;
}

function toHighlightedAreaName(name: string | null | undefined, searchText: string | undefined) {
  return name ? <HighlightText text={name} highlightedText={searchText} /> : null;
}

function renderStackedArea(area: AreaNames, searchText: string | undefined) {
  return (
    <StackedCellText
      primary={toHighlightedAreaName(area.divisionName, searchText) ?? <EmptyValue />}
      secondary={toHighlightedAreaName(area.districtName, searchText)}
      tertiary={toHighlightedAreaName(area.thanaName, searchText)}
    />
  );
}

function renderCommaSeparatedArea(area: AreaNames, searchText: string | undefined) {
  const areaText = [area.thanaName, area.districtName, area.divisionName]
    .filter((name): name is string => Boolean(name))
    .join(AREA_NAME_SEPARATOR);

  return (
    <StackedCellText
      primary={
        areaText ? <HighlightText text={areaText} highlightedText={searchText} /> : <EmptyValue />
      }
    />
  );
}

export function getAreaColumn<TRow extends AreaNames>(): ColumnDef<TRow, unknown>;
export function getAreaColumn<TRow>(
  getArea: (row: TRow) => AreaNames,
  options?: AreaColumnOptions,
): ColumnDef<TRow, unknown>;
export function getAreaColumn<TRow>(
  getArea?: (row: TRow) => AreaNames,
  options: AreaColumnOptions = {},
): ColumnDef<TRow, unknown> {
  return {
    id: 'area',
    header: options.isCommaSeparated ? COMMA_SEPARATED_AREA_HEADER : STACKED_AREA_HEADER,
    cell: ({ row }) => {
      const area = getArea ? getArea(row.original) : (row.original as AreaNames);
      return options.isCommaSeparated
        ? renderCommaSeparatedArea(area, options.searchText)
        : renderStackedArea(area, options.searchText);
    },
  };
}
